import {useMutation} from '@apollo/client';
import {
  UpdateCategoryDocument,
  useAllCategoryGroupsQuery,
  useCategoryByIdQuery,
  useSetCategoryGroupMutation,
} from '@transactions/graphql';
import * as React from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {setCategoryGroups} from 'src/apolloCache/categoryGroups';
import {useBreadcrumb} from 'src/Breadcrumbs';
import {Button} from 'src/Button';
import {
  validCategoryColorOrNull,
  validCategoryIconOrNull,
} from 'src/categoryAppearance';
import InputGroup from 'src/InputGroup';
import {CategoryAppearanceInput} from 'src/inputs/CategoryAppearanceInput';
import {CategoryGroupSelect} from 'src/inputs/CategoryGroupSelect';
import {Textarea} from 'src/inputs/Textarea';
import {TextInput} from 'src/inputs/TextInput';
import Empty from 'src/ListView/Empty';
import {useModals} from 'src/Modals';

export const CategoryDetailView = () => {
  const params = useParams();
  const navigate = useNavigate();
  const categoryId = params.id || '';
  const {deleteCategoryModal} = useModals();
  const {data, loading} = useCategoryByIdQuery({
    variables: {id: categoryId},
    skip: !categoryId,
  });

  const [name, setName] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [icon, setIcon] = React.useState<string | null>(null);
  const [color, setColor] = React.useState<string | null>(null);
  const [hasInitialized, setHasInitialized] = React.useState(false);
  // undefined = untouched (show the stored group); null = "No group"
  const [groupOverride, setGroupOverride] = React.useState<
    string | null | undefined
  >(undefined);
  const [groupError, setGroupError] = React.useState<string | null>(null);

  const [updateCategory, {loading: updateLoading, error: updateError}] =
    useMutation(UpdateCategoryDocument);
  // Track the group mutation's loading state too: on a group-only save
  // updateLoading stays false, and a second submit while the first
  // setCategoryGroup is in flight races two full-array writes (the older
  // selection can finish last and win).
  const [setCategoryGroup, {loading: groupSaving}] =
    useSetCategoryGroupMutation();
  const {data: groupsData} = useAllCategoryGroupsQuery();

  const category = data?.categoryById;
  const storedGroupId =
    (groupsData?.allCategoryGroups || []).find((group) =>
      group.categoryIds.includes(categoryId),
    )?._id ?? null;
  const groupId = groupOverride === undefined ? storedGroupId : groupOverride;

  React.useEffect(() => {
    if (category && !hasInitialized) {
      setName(category.name);
      setDescription(category.description || '');
      setIcon(validCategoryIconOrNull(category.icon));
      setColor(validCategoryColorOrNull(category.color));
      setHasInitialized(true);
    }
  }, [category, hasInitialized]);

  useBreadcrumb({
    label: category?.name || 'Loading...',
    fallback: [{label: 'Categories', href: '/categories'}],
  });

  if (!categoryId) {
    return <Empty>No category selected</Empty>;
  }

  if (loading && !data) {
    return <Empty>Loading category...</Empty>;
  }

  if (!category) {
    return <Empty>Category not found</Empty>;
  }

  const groupChanged = groupOverride !== undefined && groupOverride !== storedGroupId;
  // Tracked separately from groupChanged: a group-only save must not send
  // the ordinary fields at all, or it would overwrite edits made elsewhere
  // since this form loaded with the form's stale values.
  const fieldsChanged =
    name !== category.name ||
    description !== (category.description || '') ||
    icon !== validCategoryIconOrNull(category.icon) ||
    color !== validCategoryColorOrNull(category.color);
  const hasChanges = fieldsChanged || groupChanged;

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!hasChanges || !name.trim() || updateLoading || groupSaving) return;

    setGroupError(null);
    if (fieldsChanged) {
      await updateCategory({
        variables: {
          name: name.trim(),
          id: category._id,
          description: description.trim() || null,
          icon,
          color,
        },
      });
    }
    if (groupChanged) {
      // The generated hook exposes no error state, so an uncaught rejection
      // here would leave the select showing an unsaved value with no
      // explanation (and reset nothing).
      try {
        await setCategoryGroup({
          variables: {categoryId: category._id, groupId},
          update: (cache, result) => {
            const updated = result.data?.setCategoryGroup;
            if (updated) {
              setCategoryGroups(cache, updated);
            }
          },
        });
        setGroupOverride(undefined);
      } catch (err: unknown) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
        setGroupError((err as Error).message);
      }
    }
  };

  const handleDelete = () => {
    deleteCategoryModal.openModal({
      category,
      onDeleted: () => navigate('/categories'),
    });
  };

  const handleReset = () => {
    setName(category.name);
    setDescription(category.description || '');
    setIcon(validCategoryIconOrNull(category.icon));
    setColor(validCategoryColorOrNull(category.color));
    setGroupOverride(undefined);
    setGroupError(null);
  };

  return (
    <div className="flex flex-1 flex-col gap-6 overflow-y-auto p-4">
      <div>
        <h2 className="text-lg font-semibold">Edit Category</h2>
        <p className="text-muted-foreground text-sm">
          Update or delete this category
        </p>
      </div>

      {/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
      <form onSubmit={handleSave} className="flex max-w-md flex-col gap-4">
        <InputGroup label="Name">
          <TextInput autoFocus value={name} onChange={setName} />
        </InputGroup>

        <InputGroup label="Description">
          <Textarea
            value={description}
            onChange={setDescription}
            placeholder="Optional description for this category"
            rows={3}
          />
        </InputGroup>

        <CategoryGroupSelect value={groupId} onChange={setGroupOverride} />

        <InputGroup label="Appearance">
          <CategoryAppearanceInput
            name={name}
            icon={icon}
            color={color}
            onIconChange={setIcon}
            onColorChange={setColor}
          />
        </InputGroup>

        {updateError && (
          <div className="text-destructive text-sm">{updateError.message}</div>
        )}
        {groupError && (
          <div className="text-destructive text-sm">
            Couldn't save the group: {groupError}
          </div>
        )}

        <div className="flex gap-2">
          <Button
            variant="primary"
            type="submit"
            disabled={!hasChanges || !name.trim() || updateLoading || groupSaving}
          >
            {updateLoading || groupSaving ? 'Saving...' : 'Save'}
          </Button>
          <Button
            variant="outline"
            type="button"
            onClick={handleReset}
            disabled={!hasChanges}
          >
            Reset
          </Button>
          <Button variant="destructive" type="button" onClick={handleDelete}>
            Delete
          </Button>
        </div>
      </form>
    </div>
  );
};
